import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Observer } from 'mobx-react';
import * as React from 'react';
import {
	DraggableProvided,
	DraggableStateSnapshot,
	DroppableProvided,
	DroppableStateSnapshot,
} from 'react-beautiful-dnd';

// Re-export types for compatibility
export type { DraggableProvided, DraggableStateSnapshot, DroppableProvided, DroppableStateSnapshot };

// Legacy props interfaces for compatibility with existing code
interface DraggableProps {
	draggableId: string;
	index: number;
	type?: string;
	isDragDisabled?: boolean;
	children?: (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => React.ReactElement | null;
	disableInteractiveElementBlocking?: boolean;
	// Additional data to store in drag event
	data?: any;
}

interface DroppableProps {
	droppableId: string;
	type?: string;
	direction?: 'vertical' | 'horizontal';
	isDropDisabled?: boolean;
	className?: string;
	children?: (provided: DroppableProvided, snapshot: DroppableStateSnapshot) => React.ReactElement | null;
}

// Mock provided objects to maintain compatibility
const createMockDraggableProvided = (element: HTMLElement): DraggableProvided => ({
	innerRef: () => element,
	draggableProps: {
		style: undefined,
		onTransitionEnd: undefined,
	} as any,
	dragHandleProps: {
		'aria-describedby': '',
		role: 'button',
		tabIndex: 0,
		draggable: false,
		onDragStart: () => {
			// Empty function for compatibility
		},
	} as any,
});

const createMockDroppableProvided = (element: HTMLElement): DroppableProvided => ({
	innerRef: () => element,
	droppableProps: {} as any,
	placeholder: null,
});

const createMockSnapshot = (isDragging: boolean): DraggableStateSnapshot =>
	({
		isDragging,
		draggingOver: null,
		combineWith: null,
		combineTargetFor: null,
		mode: null,
	}) as any;

const createMockDroppableSnapshot = (isDraggingOver: boolean): DroppableStateSnapshot =>
	({
		isDraggingOver,
		draggingFromThisWith: null,
		isUsingPlaceholder: false,
	}) as any;

export const DraggableObserver: React.FunctionComponent<DraggableProps> = props => {
	const { children, draggableId, index, type, isDragDisabled, data } = props;
	const elementRef = React.useRef<HTMLDivElement>(null);
	const [isDragging, setIsDragging] = React.useState(false);

	React.useEffect(() => {
		const element = elementRef.current;
		if (!element || isDragDisabled) {
			return;
		}

		const cleanup = draggable({
			element,
			getInitialData: () => {
				const dragData = {
					type: type || 'default',
					id: draggableId,
					index,
					// Include any additional data passed to the component
					...(data || {}),
				};

				return dragData;
			},
			onDragStart: () => {
				setIsDragging(true);
			},
			onDrop: () => {
				setIsDragging(false);
			},
		});

		return cleanup;
	}, [draggableId, index, type, isDragDisabled, data]);

	const provided = createMockDraggableProvided(elementRef.current!);
	const snapshot = createMockSnapshot(isDragging);

	return (
		<div ref={elementRef}>
			<Observer>{() => children?.(provided, snapshot) || null}</Observer>
		</div>
	);
};

export const DroppableObserver: React.FunctionComponent<DroppableProps> = props => {
	const { children, droppableId, type, isDropDisabled, className } = props;
	const elementRef = React.useRef<HTMLDivElement>(null);
	const [isDraggingOver, setIsDraggingOver] = React.useState(false);

	React.useEffect(() => {
		const element = elementRef.current;
		if (!element || isDropDisabled) {
			return;
		}

		const cleanup = dropTargetForElements({
			element,
			canDrop: ({ source }) => {
				const sourceType = (source.data as any)?.type;
				const targetType = type || 'default';
				const canDrop = sourceType === targetType;
				return canDrop;
			},
			getData: () => {
				const data = {
					type: type || 'default',
					id: droppableId,
				};
				return data;
			},
			onDragEnter: () => {
				setIsDraggingOver(true);
			},
			onDragLeave: () => {
				setIsDraggingOver(false);
			},
			onDrop: () => {
				setIsDraggingOver(false);
			},
		});

		return cleanup;
	}, [droppableId, type, isDropDisabled]);

	const provided = createMockDroppableProvided(elementRef.current!);
	const snapshot = createMockDroppableSnapshot(isDraggingOver);

	return (
		<div ref={elementRef} className={className} style={{ width: '100%', position: 'relative' }}>
			<Observer>{() => children?.(provided, snapshot) || null}</Observer>
		</div>
	);
};
